import { io, Socket } from 'socket.io-client';

/**
 * WebSocket Service
 * Replaces Electron's IPC send/receive pattern with Socket.IO
 */
class WebSocketService {
  private socket: Socket | null = null;
  private subscribers: Map<string, Set<Function>> = new Map();

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.socket?.connected) {
      return; // Already connected
    }

    const wsUrl = import.meta.env.VITE_WS_URL || 'http://localhost:4000';

    this.socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected');
    });

    this.socket.on('disconnect', () => {
      console.log('❌ WebSocket disconnected');
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    // Listen for progress messages
    this.socket.on('progress:message', (data: any) => {
      this.handleProgressMessage(data);
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.subscribers.clear();
    }
  }

  /**
   * Subscribe to progress updates on a specific channel
   * Replaces: window.ipcRenderer.receive(channel, callback)
   *
   * @param channel - The channel/event name to subscribe to
   * @param callback - Function to call when progress update is received
   */
  subscribeToProgress(channel: string, callback: (data: any) => void): void {
    this.connect();

    // Add callback to subscribers
    if (!this.subscribers.has(channel)) {
      this.subscribers.set(channel, new Set());
    }
    this.subscribers.get(channel)!.add(callback);

    // Subscribe to channel on server
    this.socket?.emit('subscribe:progress', channel);
  }

  /**
   * Unsubscribe from progress updates on a specific channel
   * Replaces: window.ipcRenderer.removeAllListeners(channel)
   *
   * @param channel - The channel/event name to unsubscribe from
   */
  unsubscribeFromProgress(channel: string): void {
    // Remove all callbacks for this channel
    this.subscribers.delete(channel);

    // Unsubscribe from channel on server
    this.socket?.emit('unsubscribe:progress', channel);
  }

  /**
   * Handle incoming progress messages
   * Routes messages to appropriate channel subscribers
   */
  private handleProgressMessage(data: any): void {
    // Extract channel from data if available
    const channel = data.channel || 'default';

    // Notify all subscribers for this channel
    const callbacks = this.subscribers.get(channel);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in progress callback:', error);
        }
      });
    }
  }

  /**
   * Send a custom event to the server (if needed)
   */
  emit(event: string, data?: any): void {
    this.connect();
    this.socket?.emit(event, data);
  }

  /**
   * Listen for custom events (if needed)
   */
  on(event: string, callback: (data: any) => void): void {
    this.connect();
    this.socket?.on(event, callback);
  }

  /**
   * Remove listener for custom events
   */
  off(event: string, callback?: (data: any) => void): void {
    if (callback) {
      this.socket?.off(event, callback);
    } else {
      this.socket?.off(event);
    }
  }

  /**
   * Get connection status
   */
  get isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

// Export singleton instance
export const wsService = new WebSocketService();

// Auto-connect on module load
wsService.connect();

// Clean up on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    wsService.disconnect();
  });
}
